import React, { useState, useEffect } from 'react';
import { CircleCheckBig, CircleX, Flag, Timer } from 'lucide-react';
import { selectiveApi } from '../../services/selectiveApi';
import DOMPurify from 'dompurify';
import './QuestionDisplay.css';

/**
 * QuestionDisplay Component
 *
 * Displays questions during an active session with timer, answer submission,
 * navigation, and question palette.
 */
const QuestionDisplay = ({ session, onComplete, onError }) => {
  // State
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { questionId: selectedOption }
  const [submittedQuestions, setSubmittedQuestions] = useState(new Set()); // Track which questions have been submitted to backend
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [timeRemaining, setTimeRemaining] = useState(null); // seconds
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null); // For practice mode instant feedback
  const [questionStartTime, setQuestionStartTime] = useState(Date.now()); // Track time per question

  const questions = session.questions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const isPracticeMode = session.session_type === 'practice';
  const hasTimeLimit = session.time_limit_seconds !== null && session.time_limit_seconds > 0;

  // Timer effect
  useEffect(() => {
    if (!hasTimeLimit) return;

    // Initialize time remaining
    if (timeRemaining === null) {
      setTimeRemaining(session.time_limit_seconds);
    }

    // Countdown timer
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev === null || prev <= 0) {
          clearInterval(timer);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [hasTimeLimit, timeRemaining]);

  const handleAnswerSelect = (option) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: option
    }));
    setFeedback(null); // Clear previous feedback
  };

  const handleSubmitAnswer = async () => {
    const selectedAnswerLetter = answers[currentQuestion.id];
    if (!selectedAnswerLetter) {
      onError?.('Please select an answer');
      return;
    }

    setLoading(true);
    try {
      // Calculate time taken for this question
      const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000); // seconds

      // Find the selected option UUID from the letter
      const selectedOption = currentQuestion.options?.find(
        opt => opt.option_letter === selectedAnswerLetter
      );

      if (!selectedOption) {
        onError?.('Invalid answer selection');
        setLoading(false);
        return;
      }

      const response = await selectiveApi.sessions.submitAnswer(session.session_id, {
        question_id: currentQuestion.id,
        selected_option_id: selectedOption.id,
        time_taken_seconds: timeTaken,
        flagged_for_review: flaggedQuestions.has(currentQuestion.id)
      });

      // Mark question as submitted
      setSubmittedQuestions(prev => new Set([...prev, currentQuestion.id]));

      // Show feedback in practice mode
      if (isPracticeMode && response.feedback) {
        setFeedback({
          isCorrect: response.is_correct,
          correctAnswer: response.correct_answer,
          explanation: response.explanation
        });
      }

      // Auto-advance in mock exam mode
      if (!isPracticeMode) {
        handleNext();
      }
    } catch (err) {
      console.error('Error submitting answer:', err);
      onError?.(err.message || 'Failed to submit answer');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setFeedback(null);
    setQuestionStartTime(Date.now()); // Reset timer for next question
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setFeedback(null);
    setQuestionStartTime(Date.now()); // Reset timer when going back
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleFlag = () => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(currentQuestion.id)) {
        newSet.delete(currentQuestion.id);
      } else {
        newSet.add(currentQuestion.id);
      }
      return newSet;
    });
  };

  const handleQuestionSelect = (index) => {
    setFeedback(null);
    setQuestionStartTime(Date.now()); // Reset timer when jumping to question
    setCurrentQuestionIndex(index);
  };

  const handleCompleteSession = async () => {
    setLoading(true);

    try {
      // IMPORTANT: Submit current question answer if user selected one but didn't click submit
      const currentAnswer = answers[currentQuestion.id];
      const hasSubmittedCurrentQuestion = submittedQuestions.has(currentQuestion.id);

      if (currentAnswer && !hasSubmittedCurrentQuestion) {
        const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000);
        const selectedOption = currentQuestion.options?.find(
          opt => opt.option_letter === currentAnswer
        );

        if (selectedOption) {
          await selectiveApi.sessions.submitAnswer(session.session_id, {
            question_id: currentQuestion.id,
            selected_option_id: selectedOption.id,
            time_taken_seconds: timeTaken,
            flagged_for_review: flaggedQuestions.has(currentQuestion.id)
          });
          // Mark as submitted
          setSubmittedQuestions(prev => new Set([...prev, currentQuestion.id]));
        }
      }

      // Count truly unanswered questions (not in answers state)
      const unansweredCount = questions.length - Object.keys(answers).length;

      if (unansweredCount > 0) {
        const confirmed = window.confirm(
          `You have ${unansweredCount} unanswered question${unansweredCount !== 1 ? 's' : ''}. Are you sure you want to finish?`
        );
        if (!confirmed) {
          setLoading(false);
          return;
        }
      }

      const completionResults = await selectiveApi.sessions.complete(session.session_id);
      onComplete?.(session.session_id, completionResults);
    } catch (err) {
      console.error('Error completing session:', err);
      onError?.(err.message || 'Failed to complete session');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSubmit = async () => {
    try {
      const completionResults = await selectiveApi.sessions.complete(session.session_id);
      alert('Time is up! Your session has been automatically submitted.');
      onComplete?.(session.session_id, completionResults);
    } catch (err) {
      console.error('Error auto-submitting session:', err);
      onError?.(err.message || 'Failed to auto-submit session');
    }
  };

  const getAnswerStatus = (questionId) => {
    if (answers[questionId]) return 'answered';
    if (flaggedQuestions.has(questionId)) return 'flagged';
    return 'unanswered';
  };

  const formatTime = (seconds) => {
    if (seconds === null) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!currentQuestion) {
    return (
      <div className="question-display error">
        <p>No questions available</p>
      </div>
    );
  }

  return (
    <div className="question-display">

      {/* Header with Timer */}
      <div className="question-header">
        <div className="question-info">
          <span className="question-number">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          <span className="question-meta">
            {currentQuestion.section_name} • {currentQuestion.tier}
          </span>
        </div>
        {hasTimeLimit && (
          <div className={`timer ${timeRemaining < 300 ? 'warning' : ''}`}>
            <span className="timer-icon"><Timer size={18} /></span>
            <span className="timer-value">{formatTime(timeRemaining)}</span>
          </div>
        )}
      </div>

      {/* Question Content */}
      <div className="question-content">
        {/* Reading Passage (if present) */}
        {currentQuestion.passage_text && (
          <div className="reading-passage">
            {currentQuestion.passage_title && (
              <h4 className="passage-title">{currentQuestion.passage_title}</h4>
            )}
            <div className="passage-text">
              {currentQuestion.passage_text}
            </div>
          </div>
        )}

        <div className="question-text">
          <h3>Question {currentQuestionIndex + 1}</h3>
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentQuestion.question_text) }} />
        </div>

        {/* Diagram (if present) */}
        {currentQuestion.diagram_url && (
          <div className="question-diagram">
            <img
              src={currentQuestion.diagram_url}
              alt={currentQuestion.diagram_alt_text || "Question diagram"}
              className="diagram-image"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
                console.error('Failed to load diagram:', currentQuestion.diagram_url);
              }}
            />
            {currentQuestion.diagram_caption && (
              <p className="diagram-caption">{currentQuestion.diagram_caption}</p>
            )}
          </div>
        )}

        {/* Options */}
        <div className="options-container">
          {currentQuestion.options && currentQuestion.options.map(option => (
            <button
              key={option.id}
              className={`option-button ${answers[currentQuestion.id] === option.option_letter ? 'selected' : ''} ${
                feedback && feedback.correctAnswer === option.option_letter ? 'correct' : ''
              } ${
                feedback && answers[currentQuestion.id] === option.option_letter && !feedback.isCorrect ? 'incorrect' : ''
              }`}
              onClick={() => handleAnswerSelect(option.option_letter)}
              disabled={loading || (isPracticeMode && feedback !== null)}
            >
              <span className="option-label">{option.option_letter}</span>
              <span className="option-text">{option.option_text}</span>
            </button>
          ))}
        </div>

        {/* Feedback (Practice Mode Only) */}
        {isPracticeMode && feedback && (
          <div className={`feedback ${feedback.isCorrect ? 'correct' : 'incorrect'}`}>
            <div className="feedback-header">
              <span className="feedback-icon">
                {feedback.isCorrect
                  ? <CircleCheckBig size={22} color="#16a34a" />
                  : <CircleX size={22} color="#dc2626" />}
              </span>
              <span className="feedback-title">
                {feedback.isCorrect ? 'Correct!' : 'Incorrect'}
              </span>
            </div>
            {!feedback.isCorrect && (
              <p className="feedback-correct-answer">
                The correct answer is <strong>{feedback.correctAnswer}</strong>
              </p>
            )}
            {feedback.explanation && (
              <div className="feedback-explanation">
                <strong>Explanation:</strong>
                <p>{feedback.explanation}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation Controls */}
      <div className="question-controls">
        <div className="control-left">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0 || loading}
            className="control-button"
          >
            ← Previous
          </button>
          <button
            onClick={handleFlag}
            className={`control-button flag ${flaggedQuestions.has(currentQuestion.id) ? 'flagged' : ''}`}
          >
            <Flag size={16} className="inline-icon" /> {flaggedQuestions.has(currentQuestion.id) ? 'Unflag' : 'Flag for Review'}
          </button>
        </div>

        <div className="control-right">
          {!isPracticeMode || !feedback ? (
            <button
              onClick={handleSubmitAnswer}
              disabled={!answers[currentQuestion.id] || loading}
              className="control-button submit"
            >
              {loading ? 'Submitting...' : 'Submit Answer'}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={currentQuestionIndex === questions.length - 1}
              className="control-button next"
            >
              Next →
            </button>
          )}

          {currentQuestionIndex === questions.length - 1 && (
            <button
              onClick={handleCompleteSession}
              disabled={loading}
              className="control-button finish"
            >
              Finish Session
            </button>
          )}
        </div>
      </div>

      {/* Question Palette */}
      <div className="question-palette">
        <h4>Question Palette</h4>
        <div className="palette-grid">
          {questions.map((q, index) => (
            <button
              key={q.id}
              onClick={() => handleQuestionSelect(index)}
              className={`palette-button ${index === currentQuestionIndex ? 'current' : ''} ${getAnswerStatus(q.id)}`}
              title={`Question ${index + 1} - ${getAnswerStatus(q.id)}`}
            >
              {index + 1}
            </button>
          ))}
        </div>
        <div className="palette-legend">
          <span className="legend-item">
            <span className="legend-box answered"></span> Answered
          </span>
          <span className="legend-item">
            <span className="legend-box flagged"></span> Flagged
          </span>
        </div>
      </div>
    </div>
  );
};

export default QuestionDisplay;
